import { SalesChecklistManualRowsSection } from "./SalesChecklistManualRowsSection";
import { SalesChecklistSettingsSection } from "./SalesChecklistSettingsSection";
import { useSalesChecklistOrgEditorModel } from "./useSalesChecklistOrgEditorModel";

export function SalesChecklistOrgEditor({ organizationId, variant = "locationTools" }: { organizationId: string; variant?: "ops" | "locationTools" }) {
  const model = useSalesChecklistOrgEditorModel(organizationId);
  return (
    <div className="px-3 py-4 sm:px-5 sm:py-5 md:px-8 md:py-6 space-y-5">
      {variant === "locationTools" ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200">
          Checklist authoring now lives in the <strong>Ops → Daily checklist</strong> mode. This editor still saves to the same Ops-owned config.
        </p>
      ) : null}
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Sales daily checklist</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-3xl leading-relaxed">
          Controls the checklist on the Leads tab for this org. Custom ad platforms (anything other than Facebook / Google)
          are stored here so we can prioritize feed integrations — see <span className="font-mono">unsupported_pull_platform_keys</span>{" "}
          in the checklist API response for analytics.
        </p>
      </div>

      {model.msg ? (
        <div className={`text-sm rounded-xl px-4 py-3 border ${model.msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}>
          {model.msg.text}
        </div>
      ) : null}

      <SalesChecklistSettingsSection
        loading={model.loading}
        adsEnabled={model.adsEnabled}
        setAdsEnabled={model.setAdsEnabled}
        adsUseInOrgActivity={model.adsUseInOrgActivity}
        setAdsUseInOrgActivity={model.setAdsUseInOrgActivity}
        platformRows={model.platformRows}
        setPlatformRows={model.setPlatformRows}
        topicKeywords={model.topicKeywords}
        setTopicKeywords={model.setTopicKeywords}
        managerNudgePersist={model.managerNudgePersist}
        nudgeTitle={model.nudgeTitle}
        setNudgeTitle={model.setNudgeTitle}
        nudgeBody={model.nudgeBody}
        setNudgeBody={model.setNudgeBody}
        broadcastBusy={model.broadcastBusy}
        onSendNudge={() => void model.sendOrgNudgeBroadcast()}
        leadHygieneDays={model.leadHygieneDays}
        setLeadHygieneDays={model.setLeadHygieneDays}
        leadHygieneCadence={model.leadHygieneCadence}
        setLeadHygieneCadence={model.setLeadHygieneCadence}
        ackBlockingEnabled={model.ackBlockingEnabled}
        setAckBlockingEnabled={model.setAckBlockingEnabled}
        floodGuardrailsEnabled={model.floodGuardrailsEnabled}
        setFloodGuardrailsEnabled={model.setFloodGuardrailsEnabled}
        leadDefaultFirstFollowUpEnabled={model.leadDefaultFirstFollowUpEnabled}
        setLeadDefaultFirstFollowUpEnabled={model.setLeadDefaultFirstFollowUpEnabled}
        leadDefaultHotAction={model.leadDefaultHotAction}
        setLeadDefaultHotAction={model.setLeadDefaultHotAction}
        leadDefaultWarmAction={model.leadDefaultWarmAction}
        setLeadDefaultWarmAction={model.setLeadDefaultWarmAction}
        leadDefaultColdAction={model.leadDefaultColdAction}
        setLeadDefaultColdAction={model.setLeadDefaultColdAction}
        leadDefaultDueHoursHot={model.leadDefaultDueHoursHot}
        setLeadDefaultDueHoursHot={model.setLeadDefaultDueHoursHot}
        leadDefaultDueHoursWarm={model.leadDefaultDueHoursWarm}
        setLeadDefaultDueHoursWarm={model.setLeadDefaultDueHoursWarm}
        leadDefaultDueHoursCold={model.leadDefaultDueHoursCold}
        setLeadDefaultDueHoursCold={model.setLeadDefaultDueHoursCold}
      />

      <SalesChecklistManualRowsSection loading={model.loading} manualRows={model.manualRows} setManualRows={model.setManualRows} />

      <div className="flex flex-wrap gap-2 px-0 sm:px-1">
        <button type="button" onClick={() => void model.save()} disabled={model.loading} className="px-4 py-2.5 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">Save checklist template</button>
        <button type="button" onClick={model.resetToAppDefaults} disabled={model.loading} className="px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-zinc-800">Revert form to defaults</button>
        <button type="button" onClick={() => void model.clearConfig()} disabled={model.loading} className="px-3 py-2.5 rounded-lg border border-amber-300 text-sm text-amber-900 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-950/40">Clear org override</button>
      </div>
    </div>
  );
}

